import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { CheckCircle, XCircle, Warning, ShieldCheckered, FileLock, UserCircleGear } from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import ComplianceInvestigationModal from "./ComplianceInvestigationModal";

const STATUS_STYLE = {
  PASS: { icon: CheckCircle, fg: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  WARN: { icon: Warning,     fg: "text-amber-400",   bg: "bg-amber-500/10",   border: "border-amber-500/30" },
  FAIL: { icon: XCircle,     fg: "text-rose-400",    bg: "bg-rose-500/10",    border: "border-rose-500/30" },
};

export default function ComplianceAudit() {
  const [data, setData] = useState(null);
  const [investigating, setInvestigating] = useState(null);

  useEffect(() => {
    api.soc2().then(setData).catch(() => setData({ controls: [], gitleaks_history: [], scim_logs: [], summary: {} }));
  }, []);

  if (!data) return <div className="bg-slate-900 border border-slate-800 h-[420px] skeleton" />;

  return (
    <section
      data-testid="compliance-audit"
      className="bg-slate-900 border border-slate-800 flex flex-col"
    >
      <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
        <div>
          <h2 className="font-display text-base font-semibold text-slate-50 flex items-center gap-2">
            <ShieldCheckered weight="duotone" className="text-emerald-400" size={16} />
            Compliance &amp; Security Audit · SOC 2
          </h2>
          <p className="text-xs text-slate-500 mt-0.5 font-mono">
            SAST · Gitleaks · Data Integrity · SCIM/SSO · live for auditors
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px] font-mono">
          <span className="px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" data-testid="soc-pass-count">
            {data.summary.pass} PASS
          </span>
          {data.summary.warn > 0 && (
            <span className="px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30" data-testid="soc-warn-count">
              {data.summary.warn} WARN
            </span>
          )}
          {data.summary.fail > 0 && (
            <span className="px-2 py-0.5 rounded-sm bg-rose-500/10 text-rose-300 border border-rose-500/30">
              {data.summary.fail} FAIL
            </span>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-slate-800">
        {/* Controls */}
        <div className="p-4 space-y-2" data-testid="soc-controls">
          <div className="tracking-mono-label text-[10px] text-slate-500 mb-2">Controls</div>
          {data.controls.map((c, i) => {
            const S = STATUS_STYLE[c.status] || STATUS_STYLE.WARN;
            const Icon = S.icon;
            return (
              <button
                type="button"
                key={i}
                data-testid={`soc-control-${c.control}-${i}`}
                onClick={() => setInvestigating(c)}
                title="Click to open the investigation trace"
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-sm border text-left transition-colors hover:bg-emerald-500/10 hover:border-emerald-500/50 ${S.border} ${S.bg}`}
              >
                <Icon weight="fill" className={S.fg} size={18} aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-100 truncate">{c.name}</div>
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                    {c.control} · {c.findings} finding{c.findings === 1 ? "" : "s"} · click to investigate
                  </div>
                </div>
                <span className={`text-[10px] tracking-mono-label ${S.fg}`}>{c.status}</span>
              </button>
            );
          })}
        </div>

        {/* Gitleaks history chart */}
        <div className="p-4 flex flex-col">
          <div className="tracking-mono-label text-[10px] text-slate-500 mb-2 flex items-center gap-2">
            <FileLock weight="bold" size={12} /> Gitleaks · 14-Day Scan History
          </div>
          <div className="flex-1 min-h-[180px]" data-testid="gitleaks-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.gitleaks_history}>
                <XAxis dataKey="date" stroke="#475569" fontSize={9} tickFormatter={(d) => d.slice(5)} />
                <YAxis stroke="#475569" fontSize={9} width={20} />
                <Tooltip
                  contentStyle={{ background: "#020617", border: "1px solid #334155", fontSize: 11 }}
                  labelStyle={{ color: "#94a3b8", fontFamily: "IBM Plex Mono" }}
                />
                <Line type="monotone" dataKey="commits_scanned" stroke="#1E293B" strokeWidth={1.5} dot={false} name="Commits" />
                <Line type="monotone" dataKey="leaks_found" stroke="#f43f5e" strokeWidth={1.5} dot={{ r: 2 }} name="Leaks" />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[10px] text-slate-500 font-mono mt-1">
            14-day leaks: {data.gitleaks_history.reduce((a, b) => a + b.leaks_found, 0)} · all resolved
          </div>
        </div>

        {/* SCIM/SSO logs */}
        <div className="p-4 flex flex-col">
          <div className="tracking-mono-label text-[10px] text-slate-500 mb-2 flex items-center gap-2">
            <UserCircleGear weight="bold" size={12} /> SCIM / SSO Identity Log
          </div>
          <ul className="flex-1 overflow-auto thin-scroll max-h-[200px] space-y-2 text-[11px]" data-testid="scim-logs">
            {data.scim_logs.map((s, i) => (
              <li key={i} className="flex items-baseline gap-2">
                <span className="font-mono text-emerald-400/80 text-[10px] shrink-0">
                  {new Date(s.ts).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
                <span className="text-slate-300 truncate">
                  <span className="font-mono text-slate-500">[{s.idp}]</span> {s.action}{" "}
                  <span className="text-slate-500">→</span> {s.target}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {investigating && (
        <ComplianceInvestigationModal
          control={investigating}
          onClose={() => setInvestigating(null)}
        />
      )}
    </section>
  );
}
